// Die zwei Bahnhöfe (Vorgabe: bewusst einfach).
// Konzept §11: belebte vs. tote Höfe — Lichter an, Leute arbeiten, du gehst
// denen aus dem Weg, weil es vernünftig ist. Kein Fangen, kein Rauswurf,
// nur Anwesenheit.
//
// Wichtigste Einzelanforderung: alle STATIC_PAINT_SURFACES als Meshes mit
// userData.surfaceId + paintSurface. Ohne die gibt es keine ortsgebundene
// Information und damit keine Informationsschicht (§6.1).
//
// Kein Ortsname, kein Schild, keine Beschriftung (§4). Die Höfe müssen an
// ihrer Form unterscheidbar sein.

import * as THREE from 'three';
import { STATIC_PAINT_SURFACES } from '../../shared/consist.js';
import {
  STATIONS, trackFrame, railTopY,
} from '../../shared/track.js';
import { terrainHeight } from '../../shared/terrain.js';
import { workersAt } from '../../shared/workers.js';
import { mat, box, cylinder, STYLE_RULES } from '../render/style.js';
import { COL_SOLID, COL_STEP } from './collision.js';

const MIN = STYLE_RULES.minDetailSize;
const HIDE_DIST = 520; // m entlang s — ausblenden statt zerstören
// Weiche Positions-Interpolation der Patrouillen — sonst ruckeln sie bei
// niedrigem update-Takt. Kein Lerp pro Frame allokieren.
const WORKER_LERP = 8.0; // 1/s Richtung Ziel
const WORKER_YAW_LERP = 6.0;

// Modul-Puffer.
const _frame = {
  pos: { x: 0, y: 0, z: 0 },
  tangent: { x: 0, y: 0, z: 0 },
  right: { x: 0, y: 0, z: 0 },
  heading: 0,
};
const _pos = new THREE.Vector3();
const _right = { x: 0, y: 0, z: 0 };

export class StationSystem {
  async init(ctx) {
    this.group = new THREE.Group();
    this.group.name = 'stations';
    ctx.scene.add(this.group);

    /** @type {Map<string, THREE.Object3D>} */
    this.paintMeshes = new Map();
    /** @type {{ group: THREE.Group, midS: number }[]} */
    this.yards = [];
    /** Nachtlichter der belebten Höfe */
    this.lights = [];
    /**
     * Statische Idle-Figuren (Nord-Kontrast) + dynamische aus workersAt.
     * Dynamische: Map id → { group, torso, head, legL, legR, tx,ty,tz,tyaw }.
     */
    this.workers = [];
    /** @type {Map<string, object>} */
    this.patrolWorkers = new Map();
    this.patrolRoot = new THREE.Group();
    this.patrolRoot.name = 'patrol-workers';
    /** Kletterbare Türme: { ladderBottom, ladderTop, platformY } */
    this.climbables = [];

    // Optionale glTF-Modelle. Fehlt etwas, bleibt der Code-Pfad.
    this.assets = ctx.assets ?? null;
    if (this.assets?.loadMany) {
      await this.assets.loadMany([
        { name: 'water_tower' },
        { name: 'relay_hut' },
        { name: 'coal_bunker' },
        { name: 'yard_wall' },
        { name: 'platform' },
        { name: 'yard_lamp' },
        { name: 'barrel' },
        { name: 'pallet' },
        { name: 'cable_spool' },
        { name: 'hydrant' },
        { name: 'waiting_car' },
      ]);
    }

    // Malflächen zuerst — sie sind die Informationsschicht.
    // Bleiben Code-Planes an den STATIC_*-Positionen; Modelle sind Optik.
    this._buildPaintSurfaces(ctx);

    for (const st of STATIONS) {
      if (st.id === 'south') this._buildSouth(st, ctx);
      else if (st.id === 'north') this._buildNorth(st, ctx);
    }

    // Wasserturm am Scheitelpunkt (s≈5080) — paint surface sitzt dort,
    // und Höhe gibt Funkreichweite (§9). Muss erkletterbar sein.
    this._buildCrestWaterTower(ctx);

    // Patrouillen-Arbeiter (Weltschicht workersAt) — eigene Gruppe, kein Name/Marker.
    this.group.add(this.patrolRoot);
  }

  /** Kollisionsregister (kann fehlen — dann nur Optik). */
  _col(ctx) {
    return ctx?.collision ?? ctx?.systems?.collision ?? null;
  }

  /** Modellinstanz oder null — nie werfen. */
  _inst(name) {
    return this.assets?.instance?.(name) ?? null;
  }

  /**
   * Platziert ein freistehendes Modell am Gleisrahmen.
   * originAtFoot: true → y = Boden; false → y ist bereits Mittelpunkt.
   * hidePaint: paint_* ausblenden (Malflächen bleiben die STATIC-Planes).
   */
  _placeModel(parent, name, s, lateral, opts = {}) {
    const inst = this._inst(name);
    if (!inst) return null;
    trackFrame(s, _frame);
    const x = _frame.pos.x + _frame.right.x * lateral;
    const z = _frame.pos.z + _frame.right.z * lateral;
    const ground = terrainHeight(x, z, s);
    const root = inst.root;
    root.position.set(x, opts.y ?? ground, z);
    root.rotation.y = _frame.heading + (opts.yaw ?? 0);
    if (opts.scale != null) root.scale.setScalar(opts.scale);
    if (opts.hidePaint !== false) {
      root.traverse((o) => {
        if (o.name?.startsWith('paint_')) o.visible = false;
      });
    }
    // Materialien pro Instanz klonen, falls wir später einfärben.
    cloneMaterials(root);
    parent.add(root);
    return inst;
  }

  update(dt, ctx) {
    const s = focusS(ctx);

    for (const y of this.yards) {
      y.group.visible = Math.abs(s - y.midS) < HIDE_DIST;
    }
    // Scheitel-Turm und freistehende Malflächen: über Distanz steuern.
    if (this.crestTower) {
      this.crestTower.visible = Math.abs(s - 5080) < HIDE_DIST;
    }
    for (const mesh of this.paintMeshes.values()) {
      const def = mesh.userData.paintSurface;
      if (!def) continue;
      // Flächen, die zu einem Hof gehören, folgen dem Hof-visible;
      // freistehende (Brücke, Scheitel-Turm) einzeln.
      if (mesh.userData.tiedToYard) continue;
      mesh.visible = Math.abs(s - def.s) < HIDE_DIST + 80;
    }

    this._updateLights(ctx);
    this._animateWorkers(dt, ctx);
  }

  /**
   * Für Tests / PaintSystem: Mesh einer statischen Fläche.
   */
  getPaintMesh(surfaceId) {
    return this.paintMeshes.get(surfaceId) ?? null;
  }

  /**
   * Für Tests: alle gebauten surfaceIds.
   */
  allPaintSurfaceIds() {
    return [...this.paintMeshes.keys()];
  }

  /**
   * Für Tests: Kletterweg des Wasserturms.
   */
  getWaterTowerClimb() {
    return this.climbables.find((c) => c.id === 'tower:north-water')
      ?? this.climbables[0]
      ?? null;
  }

  // --- Statische Malflächen (§6.1) ------------------------------------------

  _buildPaintSurfaces(ctx) {
    for (const surface of STATIC_PAINT_SURFACES) {
      // Turm und Brückenpfeiler werden mit ihrer Struktur gebaut —
      // hier nur die reinen Wandflächen, außer wir bauen alles als Plane.
      // Einheitlich: dünne Box als Fläche, exakt an s/lateral/facing/Maßen.
      // Spezielle Strukturen (Turm, Brücke) fügen zusätzliche Geometrie
      // daneben, die Fläche selbst bleibt das Raycast-Ziel.
      const mesh = this._makePaintPlane(surface);
      this.group.add(mesh);
      this.paintMeshes.set(surface.surfaceId, mesh);
    }
    void ctx;
  }

  /**
   * Baut die bemalbare Fläche exakt nach STATIC_PAINT_SURFACES-Definition.
   * facing: -1 = schaut zum Gleis von +lateral, +1 von −lateral
   * (facing multipliziert die Rechts-Normale für die Wandnormale).
   */
  _makePaintPlane(surface) {
    const { s, lateral, facing, widthM, heightM, centerY, surfaceId } = surface;
    trackFrame(s, _frame);

    // Wand als dünne Box — min. 10 cm dick (Stilregel).
    const thick = Math.max(MIN, 0.12);
    const geo = box(thick, heightM, widthM);
    // Farbe je nach Art — lesbar, aber nicht bunt.
    const matKey = surface.kind === 'tower' ? 'steel'
      : surface.kind === 'pier' ? 'concreteDark'
        : surface.kind === 'relay' ? 'corrugated'
          : 'concrete';
    const mesh = new THREE.Mesh(geo, mat(matKey));

    // Position: Gleismitte + lateral * right, Höhe = trackY + centerY? 
    // centerY ist absolute Höhe über Schienen-/Geländeniveau der Fläche-Mitte.
    // railTopY als Bezug ist für Wände am Hof besser: trackPoint.y + centerY.
    const x = _frame.pos.x + _frame.right.x * lateral;
    const z = _frame.pos.z + _frame.right.z * lateral;
    const y = _frame.pos.y + centerY;

    mesh.position.set(x, y, z);

    // Ausrichtung: Fläche parallel zur Strecke (Breite längs s), Normale = ±right.
    // Box-X ist Dicke → X zeigt entlang right * facing.
    // heading dreht um Y: Standard-Box hat Z nach vorne; wir wollen Z = tangent,
    // X = right * facing.
    mesh.rotation.y = _frame.heading;
    // Wenn facing = -1, 180° um Y, damit die Vorderseite zum Gleis zeigt.
    if (facing < 0) {
      mesh.rotation.y += Math.PI;
    }

    mesh.userData.surfaceId = surfaceId;
    mesh.userData.paintSurface = surface;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = `paint:${surfaceId}`;
    return mesh;
  }

  // --- Süd: Cinder Flats (belebt, activity 0.55) -----------------------------

  _buildSouth(st, ctx) {
    const g = new THREE.Group();
    g.name = 'yard:south';
    g.userData.midS = st.s;

    // Bahnsteig
    this._platform(g, st, 48, 3.2, 0.55, ctx);

    // Hofmauer (Ostseite) — die Malfläche wall:south-yard sitzt darauf.
    this._yardWall(g, 280, 340, 13.0, 3.2, ctx);
    const wallMesh = this.paintMeshes.get('wall:south-yard');
    if (wallMesh) wallMesh.userData.tiedToYard = true;

    // Kohlebunker mit Verladeanlage (West, am Ladegleis).
    this._coalBunker(g, st, ctx);
    const bunkerPaint = this.paintMeshes.get('wall:south-bunker');
    if (bunkerPaint) bunkerPaint.userData.tiedToYard = true;

    // Stellwerk
    this._signalBox(g, 200, -11.5, 1.0, ctx);

    // Lampenmasten mit Licht — nachts brennt es.
    for (const s of [120, 220, 340, 460, 560]) {
      this._lampPost(g, s, st.platformLateral + 2.5, true, ctx);
      this._lampPost(g, s + 40, -st.platformLateral - 2.0, true, ctx);
    }

    // Container / Fässer — Hof-Inventar ohne Funktion.
    this._scatterYardProps(g, st, 0.55, ctx);

    // Keine statischen Süd-Figuren mehr: workersAt() liefert die belebten
    // Patrouillen (Konzept §11). Statuen würden mit den Gehern doppelte
    // Anwesenheit vortäuschen.

    this.group.add(g);
    this.yards.push({ group: g, midS: st.s, id: st.id });
  }

  // --- Nord: Mott Junction (halb tot, activity 0.2) --------------------------

  _buildNorth(st, ctx) {
    const g = new THREE.Group();
    g.name = 'yard:north';
    g.userData.midS = st.s;

    // Kürzerer, niedrigerer Bahnsteig — lesbar anders als Süd.
    this._platform(g, st, 32, 2.6, 0.45, ctx);

    // Hofmauer
    this._yardWall(g, 7000, 7080, -11.5, 3.0, ctx);
    const wallMesh = this.paintMeshes.get('wall:north-yard');
    if (wallMesh) wallMesh.userData.tiedToYard = true;

    // Relaishäuschen (Malfläche wall:north-relay sitzt dort).
    this._relayHut(g, 6880, 10.5, ctx);
    const relayPaint = this.paintMeshes.get('wall:north-relay');
    if (relayPaint) relayPaint.userData.tiedToYard = true;

    // Weniger Lampen, schwächer.
    for (const s of [6780, 7000, 7120]) {
      this._lampPost(g, s, st.platformLateral - 2.0, false, ctx);
    }

    // Verwilderte Gleise: Schrott, Büsche-Andeutung als dunkle Klumpen.
    this._scatterYardProps(g, st, 0.2, ctx);

    // Ein einsamer Arbeiter — Hof ist nicht ganz leer, aber tot.
    this.workers.push(this._worker(g, 7020, -8.0));

    // Hof-Wasserturm (hasWaterTower) — zusätzliche Kletterhöhe im Hof.
    // Die bemalte Fläche sitzt am Scheitel (s=5080); hier nur Silhouette + Leiter.
    this._waterTowerStructure(g, st.s + 40, -12.0, {
      id: 'tower:north-yard',
      paint: false,
      tankH: 7.2,
    }, ctx);

    this.group.add(g);
    this.yards.push({ group: g, midS: st.s, id: st.id });
  }

  // --- Wasserturm am Scheitelpunkt ------------------------------------------

  _buildCrestWaterTower(ctx) {
    // STATIC_PAINT_SURFACES: tower:north-water bei s=5080, lateral=-11.
    // Struktur drumherum + Leiter vom Boden bis zum Podest.
    const surface = STATIC_PAINT_SURFACES.find((s) => s.surfaceId === 'tower:north-water');
    if (!surface) return;

    const g = new THREE.Group();
    g.name = 'tower:crest';
    this.crestTower = g;

    const climb = this._waterTowerStructure(g, surface.s, surface.lateral, {
      id: 'tower:north-water',
      paint: true,
      tankH: 8.4,
    }, ctx);

    // Malfläche an die Tankwand koppeln — Position stimmt schon aus
    // _makePaintPlane; sicherstellen, dass sie sichtbar mit dem Turm ist.
    const paint = this.paintMeshes.get('tower:north-water');
    if (paint) {
      paint.userData.tiedToYard = false;
      // Leicht vor die Tankwand schieben ist schon durch lateral erledigt.
    }

    this.group.add(g);
    void climb;
  }

  /**
   * Wasserturm-Silhouette mit Leiter und Podest.
   * @returns {{ id, ladderBottom, ladderTop, platformY, s, lateral }}
   */
  _waterTowerStructure(parent, s, lateral, opts, ctx) {
    trackFrame(s, _frame);
    const ground = terrainHeight(
      _frame.pos.x + _frame.right.x * lateral,
      _frame.pos.z + _frame.right.z * lateral,
      s
    );
    const baseX = _frame.pos.x + _frame.right.x * lateral;
    const baseZ = _frame.pos.z + _frame.right.z * lateral;
    const platformY = ground + (opts.tankH ?? 8.0);
    const spread = 1.8;

    const model = this._inst('water_tower');
    let root;
    if (model) {
      // Modell: Ursprung = Fußmitte auf Boden. paint_* aus — STATIC-Plane bleibt.
      root = model.root;
      root.position.set(baseX, ground, baseZ);
      root.rotation.y = _frame.heading;
      cloneMaterials(root);
      root.traverse((o) => {
        if (o.name?.startsWith('paint_')) o.visible = false;
      });
      parent.add(root);
    } else {
      root = new THREE.Group();
      root.position.set(baseX, 0, baseZ);
      root.rotation.y = _frame.heading;

      const tankR = 2.6;
      const legH = platformY - ground - 1.2;

      // Vier Beine.
      const legGeo = box(0.28, Math.max(legH, MIN), 0.28);
      const legMat = mat('steelDark');
      for (const [lx, lz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        const leg = new THREE.Mesh(legGeo, legMat);
        leg.position.set(lx * spread, ground + legH * 0.5, lz * spread);
        root.add(leg);
      }

      // Kreuzstreben (Silhouette).
      for (let i = 0; i < 3; i++) {
        const y = ground + legH * (0.25 + i * 0.25);
        const beam = new THREE.Mesh(box(spread * 2.2, 0.12, 0.12), legMat);
        beam.position.set(0, y, -spread);
        root.add(beam);
        const beam2 = beam.clone();
        beam2.position.z = spread;
        root.add(beam2);
      }

      // Podest
      const deck = new THREE.Mesh(box(5.2, 0.18, 5.2), mat('woodDark'));
      deck.position.set(0, platformY - 0.5, 0);
      root.add(deck);

      // Tank
      const tank = new THREE.Mesh(
        cylinder(tankR, tankR, 3.2, 10),
        mat('steel')
      );
      tank.position.set(0, platformY + 1.0, 0);
      root.add(tank);

      // Dachkegel (abgestumpft).
      const roof = new THREE.Mesh(
        cylinder(0.4, tankR * 0.95, 1.0, 10),
        mat('railRust')
      );
      roof.position.set(0, platformY + 3.0, 0);
      root.add(roof);

      parent.add(root);
    }

    // Leiter: immer als Logik-Objekt (Klettern + Tests). Beim Modell zusätzlich
    // unsichtbar, weil die Silhouette schon im Mesh steckt.
    const ladderH = platformY - 0.5 - ground;
    const ladder = this._ladder(ladderH);
    ladder.position.set(spread + 0.35, ground + ladderH * 0.5, 0);
    ladder.name = 'ladder';
    ladder.userData.climb = {
      bottomY: ground,
      topY: platformY - 0.5,
      height: ladderH,
    };
    if (model) ladder.visible = false;
    // Am parent in Weltkoordinaten relativ zum Turm-Root anhängen.
    // root hat y=ground (Modell) oder y=0 (Code) — Leiter-y muss passen.
    if (model) {
      // root.y = ground → Leiter-Position relativ: y = ladderH/2, x = spread
      ladder.position.set(spread + 0.35, ladderH * 0.5, 0);
    }
    root.add(ladder);

    const info = {
      id: opts.id,
      s,
      lateral,
      ladderBottom: ground,
      ladderTop: platformY - 0.5,
      platformY: platformY - 0.5,
      ladder,
      root,
    };
    this.climbables.push(info);

    // Kollision: Tank + Beinkörper als Zylinder — Leiter (spread+0.35) bleibt frei.
    const col = this._col(ctx);
    if (col) {
      const tankR = 2.5;
      const tankCy = platformY + 1.0;
      col.addCylinder(baseX, tankCy, baseZ, tankR, 1.8, COL_SOLID);
      // Unterbau als schmaler Kern (nicht bis zu den Beinen — Klettern).
      const coreH = (platformY - 0.5 - ground) * 0.5;
      if (coreH > 0.5) {
        col.addCylinder(baseX, ground + coreH, baseZ, 0.9, coreH, COL_SOLID);
      }
      // Podest begehbar
      col.addBox(baseX, platformY - 0.5, baseZ, 2.4, 0.12, 2.4, _frame.heading, COL_SOLID | COL_STEP);
    }
    return info;
  }

  _ladder(height) {
    const g = new THREE.Group();
    const railMat = mat('steel');
    const h = Math.max(height, MIN);
    // Holme
    const railL = new THREE.Mesh(box(0.1, h, 0.1), railMat);
    railL.position.x = -0.25;
    const railR = new THREE.Mesh(box(0.1, h, 0.1), railMat);
    railR.position.x = 0.25;
    g.add(railL, railR);
    // Sprossen alle 0.35 m — ≥10 cm dick.
    const step = 0.35;
    const n = Math.max(2, Math.floor(h / step));
    for (let i = 0; i < n; i++) {
      const r = new THREE.Mesh(box(0.55, 0.1, 0.1), railMat);
      r.position.y = -h * 0.5 + (i + 0.5) * (h / n);
      g.add(r);
    }
    return g;
  }

  // --- Hof-Bauteile ---------------------------------------------------------

  _platform(parent, st, length, width, height, ctx) {
    trackFrame(st.s, _frame);
    const lat = st.platformLateral;
    const x0 = _frame.pos.x + _frame.right.x * lat;
    const z0 = _frame.pos.z + _frame.right.z * lat;
    // Oberkante knapp unter Waggonboden.
    const yCenter = railTopY(st.s, lat) + height * 0.5 - 0.15;

    // Kachelbares Plattform-Segment (6 m), falls geladen.
    const segLen = 6.0;
    const nSeg = Math.max(1, Math.round(length / segLen));
    let usedModel = false;
    if (this.assets?.models?.has('platform')) {
      usedModel = true;
      for (let i = 0; i < nSeg; i++) {
        const s = st.s + (i - (nSeg - 1) * 0.5) * segLen;
        const inst = this._inst('platform');
        if (!inst) { usedModel = false; break; }
        trackFrame(s, _frame);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const ground = railTopY(s, lat) - 0.15;
        inst.root.position.set(x, ground, z);
        // Gleisseite des Modells liegt bei -X; bei negativem lateral spiegeln.
        inst.root.rotation.y = _frame.heading + (lat < 0 ? Math.PI : 0);
        cloneMaterials(inst.root);
        parent.add(inst.root);
      }
    }

    if (!usedModel) {
      const mesh = new THREE.Mesh(box(width, height, length), mat('concrete'));
      mesh.position.set(x0, yCenter, z0);
      mesh.rotation.y = _frame.heading;
      mesh.receiveShadow = true;
      mesh.castShadow = true;
      parent.add(mesh);

      // Bahnsteigkante dunkler.
      const edge = new THREE.Mesh(
        box(0.2, height + 0.05, length),
        mat('concreteDark')
      );
      const edgeLat = lat > 0 ? lat - width * 0.5 : lat + width * 0.5;
      edge.position.set(
        _frame.pos.x + _frame.right.x * edgeLat,
        yCenter,
        _frame.pos.z + _frame.right.z * edgeLat
      );
      edge.rotation.y = _frame.heading;
      parent.add(edge);
    }

    // Kollision: begehbare Box (STEP) — Maße aus dem Bau, nicht aus dem Mesh.
    const col = this._col(ctx);
    if (col) {
      col.addTrackBox(st.s, lat, width * 0.5, height * 0.5, length * 0.5, {
        cy: yCenter,
        flags: COL_SOLID | COL_STEP,
      });
    }
  }

  _yardWall(parent, s0, s1, lateral, height, ctx) {
    const mid = (s0 + s1) * 0.5;
    const len = s1 - s0;
    // 6-m-Segmente aus dem Modell, sonst ein langer Kasten.
    const segLen = 6.0;
    if (this.assets?.models?.has('yard_wall')) {
      const n = Math.max(1, Math.round(len / segLen));
      for (let i = 0; i < n; i++) {
        const s = s0 + (i + 0.5) * (len / n);
        const inst = this._inst('yard_wall');
        if (!inst) break;
        trackFrame(s, _frame);
        const x = _frame.pos.x + _frame.right.x * lateral;
        const z = _frame.pos.z + _frame.right.z * lateral;
        const y = terrainHeight(x, z, s);
        inst.root.position.set(x, y, z);
        inst.root.rotation.y = _frame.heading;
        // paint_* aus — die STATIC-Plane deckt die ganze Mauer ab.
        inst.root.traverse((o) => {
          if (o.name?.startsWith('paint_')) o.visible = false;
        });
        cloneMaterials(inst.root);
        parent.add(inst.root);
      }
    } else {
      trackFrame(mid, _frame);
      const mesh = new THREE.Mesh(
        box(0.35, height, len),
        mat('concreteDark')
      );
      const x = _frame.pos.x + _frame.right.x * lateral;
      const z = _frame.pos.z + _frame.right.z * lateral;
      const y = _frame.pos.y + height * 0.5;
      mesh.position.set(x, y, z);
      mesh.rotation.y = _frame.heading;
      mesh.castShadow = true;
      parent.add(mesh);
    }

    // Eine durchgehende Box — teurer pro Segment wäre verschwendet (nie bewegt).
    const col = this._col(ctx);
    if (col) {
      trackFrame(mid, _frame);
      const cy = _frame.pos.y + height * 0.5;
      col.addTrackBox(mid, lateral, 0.22, height * 0.5, len * 0.5, {
        cy,
        flags: COL_SOLID,
      });
    }
  }

  _coalBunker(parent, st, ctx) {
    // Großer Bunker am Ladegleis — Süd hat bunker paint bei lat -12.
    const s = 480;
    const lat = -12.0;
    const col = this._col(ctx);

    if (this._placeModel(parent, 'coal_bunker', s, lat)) {
      // Kohlehaufen daneben bleibt Code — lesbare Silhouette am Boden.
      trackFrame(s - 8, _frame);
      const coal = new THREE.Mesh(box(4.0, 1.4, 5.0), mat('coal'));
      coal.position.set(
        _frame.pos.x + _frame.right.x * (lat - 1),
        _frame.pos.y + 0.7,
        _frame.pos.z + _frame.right.z * (lat - 1)
      );
      coal.rotation.y = _frame.heading;
      parent.add(coal);
      if (col) {
        trackFrame(s, _frame);
        col.addTrackBox(s, lat, 3.0, 2.75, 5.0, {
          cy: _frame.pos.y + 2.9,
          flags: COL_SOLID,
        });
        col.addTrackBox(s - 8, lat - 1, 2.0, 0.7, 2.5, {
          cy: _frame.pos.y + 0.7,
          flags: COL_SOLID | COL_STEP,
        });
      }
      void st;
      return;
    }

    trackFrame(s, _frame);

    const body = new THREE.Mesh(box(6.0, 5.5, 10.0), mat('corrugated'));
    body.position.set(
      _frame.pos.x + _frame.right.x * lat,
      _frame.pos.y + 2.9,
      _frame.pos.z + _frame.right.z * lat
    );
    body.rotation.y = _frame.heading;
    body.castShadow = true;
    parent.add(body);

    // Verladetrichter über dem Gleis.
    const chute = new THREE.Mesh(box(2.4, 1.8, 3.5), mat('steelDark'));
    chute.position.set(
      _frame.pos.x + _frame.right.x * (lat + 4.5),
      _frame.pos.y + 4.2,
      _frame.pos.z + _frame.right.z * (lat + 4.5)
    );
    chute.rotation.y = _frame.heading;
    parent.add(chute);

    // Stützen
    for (const ds of [-3, 3]) {
      trackFrame(s + ds, _frame);
      const post = new THREE.Mesh(box(0.35, 4.5, 0.35), mat('steelDark'));
      post.position.set(
        _frame.pos.x + _frame.right.x * (lat + 4.5),
        _frame.pos.y + 2.3,
        _frame.pos.z + _frame.right.z * (lat + 4.5)
      );
      parent.add(post);
    }

    // Kohlehaufen
    const coal = new THREE.Mesh(box(4.0, 1.4, 5.0), mat('coal'));
    trackFrame(s - 8, _frame);
    coal.position.set(
      _frame.pos.x + _frame.right.x * (lat - 1),
      _frame.pos.y + 0.7,
      _frame.pos.z + _frame.right.z * (lat - 1)
    );
    coal.rotation.y = _frame.heading;
    parent.add(coal);

    if (col) {
      trackFrame(s, _frame);
      col.addTrackBox(s, lat, 3.0, 2.75, 5.0, {
        cy: _frame.pos.y + 2.9,
        flags: COL_SOLID,
      });
      // Stützen als dünne Zylinder
      for (const ds of [-3, 3]) {
        trackFrame(s + ds, _frame);
        const px = _frame.pos.x + _frame.right.x * (lat + 4.5);
        const pz = _frame.pos.z + _frame.right.z * (lat + 4.5);
        col.addCylinder(px, _frame.pos.y + 2.3, pz, 0.22, 2.25, COL_SOLID);
      }
      trackFrame(s - 8, _frame);
      col.addTrackBox(s - 8, lat - 1, 2.0, 0.7, 2.5, {
        cy: _frame.pos.y + 0.7,
        flags: COL_SOLID | COL_STEP,
      });
    }
    void st;
  }

  _signalBox(parent, s, lateral, activity, ctx) {
    trackFrame(s, _frame);
    const body = new THREE.Mesh(box(4.2, 3.6, 5.5), mat('woodOld'));
    body.position.set(
      _frame.pos.x + _frame.right.x * lateral,
      _frame.pos.y + 1.9,
      _frame.pos.z + _frame.right.z * lateral
    );
    body.rotation.y = _frame.heading;
    body.castShadow = true;
    parent.add(body);

    // Flachdach
    const roof = new THREE.Mesh(box(4.8, 0.25, 6.0), mat('railRust'));
    roof.position.copy(body.position);
    roof.position.y += 2.0;
    roof.rotation.y = _frame.heading;
    parent.add(roof);

    const col = this._col(ctx);
    if (col) {
      col.addTrackBox(s, lateral, 2.1, 1.8, 2.75, {
        cy: _frame.pos.y + 1.9,
        flags: COL_SOLID,
      });
    }

    // Fensterlicht nachts.
    this._lampPost(parent, s, lateral + 3.0, activity > 0.4, ctx);
  }

  _relayHut(parent, s, lateral, ctx) {
    const placed = this._placeModel(parent, 'relay_hut', s, lateral);
    if (placed) {
      trackFrame(s, _frame);
      const x = _frame.pos.x + _frame.right.x * lateral;
      const z = _frame.pos.z + _frame.right.z * lateral;
      const y = terrainHeight(x, z, s);
      this._attachLight(x, y + 2.8, z, 0.4);
      const col = this._col(ctx);
      if (col) {
        col.addTrackBox(s, lateral, 1.75, 1.2, 2.0, {
          cy: y + 1.3,
          flags: COL_SOLID,
        });
      }
      return;
    }

    trackFrame(s, _frame);
    const body = new THREE.Mesh(box(3.5, 2.4, 4.0), mat('corrugated'));
    body.position.set(
      _frame.pos.x + _frame.right.x * lateral,
      _frame.pos.y + 1.3,
      _frame.pos.z + _frame.right.z * lateral
    );
    body.rotation.y = _frame.heading;
    body.castShadow = true;
    parent.add(body);

    this._attachLight(
      body.position.x,
      body.position.y + 1.5,
      body.position.z,
      0.4
    );

    const col = this._col(ctx);
    if (col) {
      col.addTrackBox(s, lateral, 1.75, 1.2, 2.0, {
        cy: _frame.pos.y + 1.3,
        flags: COL_SOLID,
      });
    }
  }

  _lampPost(parent, s, lateral, bright, ctx) {
    trackFrame(s, _frame);
    const x = _frame.pos.x + _frame.right.x * lateral;
    const z = _frame.pos.z + _frame.right.z * lateral;
    const ground = terrainHeight(x, z, s);

    const inst = this._inst('yard_lamp');
    if (inst) {
      // Ausleger zeigt nach -X (Gleisseite): bei positivem lateral um 180° drehen.
      inst.root.position.set(x, ground, z);
      inst.root.rotation.y = _frame.heading + (lateral > 0 ? Math.PI : 0);
      cloneMaterials(inst.root);
      parent.add(inst.root);
      this._attachLight(x, ground + 8.0, z, bright ? 1.1 : 0.35);
    } else {
      const post = new THREE.Mesh(cylinder(0.1, 0.14, 6.5, 6), mat('steelDark'));
      post.position.set(x, ground + 3.25, z);
      parent.add(post);

      const head = new THREE.Mesh(box(0.55, 0.2, 0.55), mat('steel'));
      head.position.set(x, ground + 6.4, z);
      parent.add(head);

      this._attachLight(x, ground + 6.2, z, bright ? 1.1 : 0.35);
    }

    // Dünner Mast — man läuft nicht durch den Pfosten.
    const col = this._col(ctx);
    if (col) col.addCylinder(x, ground + 3.25, z, 0.14, 3.25, COL_SOLID);
  }

  _scatterYardProps(parent, st, activity, ctx) {
    const n = activity > 0.4 ? 10 : 5;
    const span = st.yardEnd - st.yardStart;
    const col = this._col(ctx);
    for (let i = 0; i < n; i++) {
      const s = st.yardStart + span * (0.15 + (i / n) * 0.7);
      const lat = (i % 2 === 0 ? 1 : -1) * (10 + (i % 3));
      trackFrame(s, _frame);
      const x = _frame.pos.x + _frame.right.x * lat;
      const z = _frame.pos.z + _frame.right.z * lat;
      const y = terrainHeight(x, z, s);

      if (i % 3 === 0) {
        // Spule oder Container-Ersatz
        const spool = this._inst('cable_spool');
        if (spool) {
          spool.root.position.set(x, y, z);
          spool.root.rotation.y = _frame.heading + i * 0.2;
          cloneMaterials(spool.root);
          parent.add(spool.root);
        } else {
          const c = new THREE.Mesh(box(2.4, 2.2, 5.5), mat('steel'));
          c.position.set(x, y + 1.1, z);
          c.rotation.y = _frame.heading + (i * 0.2);
          parent.add(c);
        }
        if (col) {
          col.addBox(x, y + 1.1, z, 1.2, 1.1, 2.75, _frame.heading + i * 0.2, COL_SOLID);
        }
      } else if (i % 3 === 1) {
        // Fässer
        for (let b = 0; b < 3; b++) {
          const barrel = this._inst('barrel');
          if (barrel) {
            barrel.root.position.set(x + b * 0.7, y, z);
            cloneMaterials(barrel.root);
            parent.add(barrel.root);
          } else {
            const mesh = new THREE.Mesh(
              cylinder(0.3, 0.3, 0.85, 8),
              mat('railRust')
            );
            mesh.position.set(x + b * 0.7, y + 0.42, z);
            parent.add(mesh);
          }
          if (col) col.addCylinder(x + b * 0.7, y + 0.42, z, 0.32, 0.45, COL_SOLID);
        }
      } else {
        // Palette / Hydrant
        const pallet = this._inst(i % 2 === 0 ? 'pallet' : 'hydrant');
        if (pallet) {
          pallet.root.position.set(x, y, z);
          pallet.root.rotation.y = _frame.heading;
          cloneMaterials(pallet.root);
          parent.add(pallet.root);
        } else {
          const crate = new THREE.Mesh(box(1.2, 0.9, 1.2), mat('woodOld'));
          crate.position.set(x, y + 0.45, z);
          crate.rotation.y = _frame.heading;
          parent.add(crate);
        }
        if (col) {
          col.addBox(x, y + 0.45, z, 0.6, 0.45, 0.6, _frame.heading, COL_SOLID | COL_STEP);
        }
      }
    }
  }

  /**
   * Minimalfigur in Warnkleidung — Körper + Kopf, leichte Idle-Bewegung.
   * Stil wie remote_players.js: blockig, Palette, ≥10 cm. Keine Namen/Marker.
   */
  _worker(parent, s, lateral) {
    trackFrame(s, _frame);
    const x = _frame.pos.x + _frame.right.x * lateral;
    const z = _frame.pos.z + _frame.right.z * lateral;
    const y = terrainHeight(x, z, s);

    const fig = this._spawnWorkerFigure();
    fig.group.position.set(x, y, z);
    fig.group.rotation.y = _frame.heading + Math.PI; // schaut Richtung Gleis
    parent.add(fig.group);
    return {
      group: fig.group,
      phase: (s * 0.17) % (Math.PI * 2),
      baseY: y,
    };
  }

  /**
   * Blockige Figur wie Remote-Spieler (remote_players.js) — geteilter Stil,
   * keine Sichtkegel, keine Labels (Konzept §8 / §11).
   */
  _spawnWorkerFigure() {
    const g = new THREE.Group();
    // Warnweste (clay) statt cloth — Hofarbeiter lesbar anders als Remote-Peers.
    const torso = new THREE.Mesh(box(0.42, 0.55, 0.28), mat('clay'));
    torso.position.y = 1.05;
    torso.castShadow = true;
    const head = new THREE.Mesh(box(0.28, 0.28, 0.28), mat('skin'));
    head.position.y = 1.48;
    head.castShadow = true;
    const legL = new THREE.Mesh(box(0.16, 0.55, 0.18), mat('denim'));
    legL.position.set(-0.11, 0.40, 0);
    const legR = new THREE.Mesh(box(0.16, 0.55, 0.18), mat('denim'));
    legR.position.set(0.11, 0.40, 0);
    // Helm — Silhouette Hofarbeit
    const helm = new THREE.Mesh(box(0.32, 0.14, 0.34), mat('sandLight'));
    helm.position.y = 1.68;
    g.add(torso, head, legL, legR, helm);
    return { group: g, torso, head, legL, legR, helm };
  }

  _attachLight(x, y, z, baseIntensity) {
    const light = new THREE.PointLight(0xffb070, 0, 32, 2.0);
    light.position.set(x, y, z);
    light.visible = false;
    this.group.add(light);
    this.lights.push({ light, baseIntensity });
  }

  _updateLights(ctx) {
    const dayness = ctx.sun?.colors?.dayness
      ?? (ctx.sun?.elevation != null ? (ctx.sun.elevation + 1) * 0.5 : 1);
    const night = dayness < 0.28;
    const t = night ? clamp01(1 - dayness / 0.28) : 0;
    for (const L of this.lights) {
      L.light.visible = t > 0.05;
      L.light.intensity = L.baseIntensity * t * 2.0;
    }
  }

  _animateWorkers(dt, ctx) {
    // Statische Idle-Figuren (Nord): leichte Bewegung, keine Absicht.
    const t = (ctx.worldSeconds ?? 0) * 0.7;
    for (const w of this.workers) {
      const bob = Math.sin(t + w.phase) * 0.02;
      w.group.position.y = w.baseY + bob;
      w.group.rotation.y += Math.sin(t * 0.3 + w.phase) * 0.0008;
    }

    // Dynamische Patrouillen aus der Weltschicht — nur Anwesenheit (§11).
    this._updatePatrolWorkers(dt, ctx);
  }

  /**
   * workersAt(worldSeconds) → Figuren. Weich interpolieren, damit sie bei
   * Fahrplan-Ticks nicht teleportieren. Kein Name, kein Marker, kein Kegel.
   */
  _updatePatrolWorkers(dt, ctx) {
    const sFocus = focusS(ctx);
    // Nur zeichnen, wenn Südhof in Reichweite — sonst Draw Calls sparen.
    const south = STATIONS[0];
    const nearSouth = Math.abs(sFocus - south.s) < HIDE_DIST;
    this.patrolRoot.visible = nearSouth;
    if (!nearSouth) return;

    const list = workersAt(ctx.worldSeconds ?? 0);
    const seen = new Set();
    const aPos = 1 - Math.exp(-WORKER_LERP * Math.max(dt, 0));
    const aYaw = 1 - Math.exp(-WORKER_YAW_LERP * Math.max(dt, 0));

    for (const w of list) {
      seen.add(w.id);
      let fig = this.patrolWorkers.get(w.id);
      if (!fig) {
        fig = this._spawnWorkerFigure();
        fig.tx = w.x;
        fig.ty = w.y;
        fig.tz = w.z;
        fig.tyaw = w.heading;
        fig.group.position.set(w.x, w.y, w.z);
        fig.group.rotation.y = w.heading;
        this.patrolRoot.add(fig.group);
        this.patrolWorkers.set(w.id, fig);
      }

      // Ziel aus Weltschicht; Anzeige lerpt (kein Ruckeln).
      fig.tx = w.x;
      fig.ty = w.y;
      fig.tz = w.z;
      fig.tyaw = w.heading;

      const g = fig.group;
      g.position.x += (fig.tx - g.position.x) * aPos;
      g.position.y += (fig.ty - g.position.y) * aPos;
      g.position.z += (fig.tz - g.position.z) * aPos;
      // Yaw kürzester Weg
      let dyaw = fig.tyaw - g.rotation.y;
      while (dyaw > Math.PI) dyaw -= Math.PI * 2;
      while (dyaw < -Math.PI) dyaw += Math.PI * 2;
      g.rotation.y += dyaw * aYaw;

      // Geh-Pose: Beine leicht versetzt wenn moving.
      if (w.moving) {
        const phase = (ctx.worldSeconds ?? 0) * 3.2 + (w.id.length * 0.7);
        fig.legL.rotation.x = Math.sin(phase) * 0.35;
        fig.legR.rotation.x = Math.sin(phase + Math.PI) * 0.35;
      } else {
        fig.legL.rotation.x = 0;
        fig.legR.rotation.x = 0;
      }
      fig.group.visible = true;
    }

    // Schichtende: Figur ausblenden/entfernen (Nacht-Filter in workersAt).
    for (const [id, fig] of this.patrolWorkers) {
      if (!seen.has(id)) {
        this.patrolRoot.remove(fig.group);
        this.patrolWorkers.delete(id);
      }
    }
  }

  dispose() {
    for (const L of this.lights) L.light.dispose?.();
    this.group?.parent?.remove(this.group);
    this.paintMeshes.clear();
  }
}

// --- Helfer -----------------------------------------------------------------

function focusS(ctx) {
  if (ctx.playerState?.trackS != null) return ctx.playerState.trackS;
  if (ctx.trackHint != null && ctx.trackHint >= 0) return ctx.trackHint;
  return STATIONS[0].s;
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Materialien pro Instanz klonen — shared glTF-Materials nicht mutieren. */
function cloneMaterials(root) {
  const map = new Map();
  root.traverse((obj) => {
    if (!obj.isMesh || !obj.material) return;
    const list = Array.isArray(obj.material) ? obj.material : [obj.material];
    const next = list.map((m) => {
      if (!m) return m;
      if (!map.has(m.uuid)) map.set(m.uuid, m.clone());
      return map.get(m.uuid);
    });
    obj.material = Array.isArray(obj.material) ? next : next[0];
  });
}
